import React from "react";
import { Form, Input, Spin } from "antd";
import { DropdownInput } from "components";
import { EComModal } from "components/EComModal";
import { AiccDefaultScenario, AiccDefaultScenarioPayload } from "types/model/aiccLearningAssets";
import { trimValues, useAssetFormModal } from "../hooks/useAssetFormModal";
import { AICC_VOICE_GENDER, AICC_VOICE_GENDER_LABEL } from "./configs";
import styles from "../LearningAssets.module.scss";

const MAX_LENGTH = {
  title: 50,
  description: 300,
  contextDescription: 1000,
};

const VOICE_GENDER_OPTIONS = [
  { label: AICC_VOICE_GENDER_LABEL[AICC_VOICE_GENDER.male], value: AICC_VOICE_GENDER.male },
  { label: AICC_VOICE_GENDER_LABEL[AICC_VOICE_GENDER.female], value: AICC_VOICE_GENDER.female },
];

interface Props {
  visible: boolean;
  scenario?: AiccDefaultScenario;
  isSaving: boolean;
  onCancel: () => void;
  onSubmit: (payload: AiccDefaultScenarioPayload) => void;
}

const toFormValues = (scenario?: AiccDefaultScenario) => ({
  title: scenario?.title,
  learnerContextDescription: scenario?.learnerContextDescription,
  aiContextDescription: scenario?.aiContextDescription,
  voiceGender: scenario?.voiceGender ?? AICC_VOICE_GENDER.male,
});

function DefaultScenarioModal({ visible, scenario, isSaving, onCancel, onSubmit }: Props) {
  const form = useAssetFormModal<AiccDefaultScenarioPayload, AiccDefaultScenario>(visible, scenario, toFormValues);
  const isEditing = !!scenario;

  const handleOk = () => form.validateFields().then((values) => onSubmit(trimValues(values)));

  return (
    <EComModal
      visible={visible}
      title={isEditing ? "Edit Default Scenario" : "Add Default Scenario"}
      okText="Save"
      onOk={handleOk}
      onCancel={onCancel}
      isSaving={isSaving}
    >
      <Spin spinning={isSaving} size="large">
        <Form form={form} layout="vertical">
          <Form.Item
            label="Scenario Title"
            name="title"
            rules={[{ required: true, message: "Please enter a scenario title." }]}
          >
            <Input placeholder="e.g. Ordering at a Cafe" maxLength={MAX_LENGTH.title} />
          </Form.Item>
          <Form.Item
            label="Learner Context Description"
            name="learnerContextDescription"
            rules={[{ required: true, message: "Please enter a learner context description." }]}
          >
            <Input.TextArea
              rows={3}
              maxLength={MAX_LENGTH.description}
              showCount
              placeholder="It's morning at a cozy neighborhood cafe. The barista greets you warmly."
            />
          </Form.Item>
          <Form.Item
            label="AI Context Description"
            name="aiContextDescription"
            rules={[{ required: true, message: "Please enter an AI context description." }]}
          >
            <Input.TextArea
              rows={4}
              maxLength={MAX_LENGTH.contextDescription}
              showCount
              placeholder="You are a friendly barista named Maria. The user wants to practice ordering coffee."
            />
          </Form.Item>
          <Form.Item
            className={styles.dropdownFormItem}
            label="Voice Gender"
            name="voiceGender"
            rules={[{ required: true, message: "Please select a voice gender." }]}
          >
            <DropdownInput options={VOICE_GENDER_OPTIONS} placeholder="Voice Gender" mode="single" />
          </Form.Item>
        </Form>
      </Spin>
    </EComModal>
  );
}

export default DefaultScenarioModal;
